<template>
    <div>
        <portal to="page-header">{{ $t('Stats Per Game') }}</portal>

        <portal to="actions">
            <a  class="px-4 py-2 font-medium uppercase leading-5 text-white bg-purple-600 rounded border border-transparent active:bg-purple-600 hover:bg-purple-700 focus:outline-none focus:shadow-outline-gray whitespace-no-wrap" :href="route('stats.perGame.export', { from, to})" target="_blank">
                <span>{{ $t('Export') }}</span>
            </a>
        </portal>

        <div class="flex flex-wrap">
            <div class="sm:px-1 md:px-3 lg:px-3 xl:px-3 w-full">
                <div class="flex justify-end">
                    <date-picker
                        :value="value"
                        @change="handleChange"
                    />
                </div>
                <template v-if="stats.length > 0 || Object.entries(stats).length > 0">
                    <div ref="statsTable" v-for="(statsByCurrency, currencyCode) in stats" class="w-full overflow-hidden shadow-xs mb-3">
                        <div class="w-full overflow-x-auto rounded-t rounded-b">
                            <table class="w-full whitespace-no-wrap">
                                <thead>
                                <tr class="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b dark:border-gray-700 bg-gray-50 dark:text-gray-400 dark:bg-gray-800">
                                    <th class="px-4 py-3 text-left">{{ $t('From') }}</th>
                                    <th class="px-4 py-3 text-left">{{ $t('To') }}</th>
                                    <th class="px-4 py-3 text-left cursor-pointer" @click="setOrder('game_name')">
                                        {{ $t('Game Name') }}
                                        <sort-icons
                                            :type="'alpha'"
                                            :display="order === 'game_name'"
                                            :direction="direction"
                                        />
                                    </th>
                                    <th class="px-4 py-3 text-right cursor-pointer" @click="setOrder('spins')">
                                        {{ $t('Spins') }} <sort-icons :display="order === 'spins'" :direction="direction" />
                                    </th>
                                    <th class="px-4 py-3 text-right cursor-pointer" @click="setOrder('stakes')">
                                        {{ $t('Bets') }} <sort-icons :display="order === 'stakes'" :direction="direction" />
                                    </th>
                                    <th class="px-4 py-3 text-right cursor-pointer" @click="setOrder('wins')">
                                        {{ $t('Wins') }} <sort-icons :display="order === 'wins'" :direction="direction" />
                                    </th>
                                    <th class="px-4 py-3 text-right cursor-pointer" @click="setOrder('hold')">
                                        {{ $t('Hold') }} <sort-icons :display="order === 'hold'" :direction="direction" />
                                    </th>
                                    <th class="px-4 py-3 text-right cursor-pointer" @click="setOrder('avg_stake_per_spin')">
                                         <sort-icons :display="order === 'avg_stake_per_spin'" :direction="direction" />
                                    </th>
                                    <th class="px-4 py-3 text-right">
                                        {{ $t('Stake/Spin') }}
                                    </th>
                                </tr>
                                </thead>
                                <tbody class="bg-gray-200 dark:bg-gray-800">
                                <tr v-for="(row, index) in statsByCurrency" :key="currencyCode + index" class="text-gray-700 dark:text-gray-400">
                                    <td class="px-4 py-3 text-left">
                                        {{ from }}
                                    </td>
                                    <td class="px-4 py-3 text-left">
                                        {{ to }}
                                    </td>
                                    <td class="px-4 py-3 text-left">
                                        {{ row.game_name }}
                                    </td>
                                    <td class="px-4 py-3 text-right">
                                        {{ row.spins }}
                                    </td>
                                    <td class="px-4 py-3 text-right">
                                        {{ showFormattedAmount(row.stakes, row.currency_code) }}
                                    </td>
                                    <td class="px-4 py-3 text-right">
                                        {{ showFormattedAmount(row.wins, row.currency_code) }}
                                    </td>
                                    <td class="px-4 py-3 text-right">
                                        {{ showFormattedAmount(row.hold, row.currency_code) }}
                                    </td>
                                    <td class="px-4 py-3 text-right">
                                        {{ row.stake_win_percentage }}
                                    </td>
                                    <td class="px-4 py-3 text-right">
                                        {{ showFormattedAmount(row.avg_stake_per_spin, row.currency_code) }}
                                    </td>
                                </tr>
                                </tbody>
                                <tfoot>
                                <tr class="text-gray-700 dark:text-gray-400 bg-gray-50 dark:bg-gray-800" v-for="sum in sums" v-if="sum.currency_code === currencyCode">
                                    <td colspan="3" class="px-4 py-3 text-left">{{ $t('Totals') }}</td>
                                    <td class="px-4 py-3 text-right font-bold">
                                        {{ sum.spins }}
                                    </td>
                                    <td class="px-4 py-3 text-right font-bold">
                                        {{ showFormattedAmount(sum.stakes, sum.currency_code) }}
                                    </td>
                                    <td class="px-4 py-3 text-right font-bold">
                                        {{ showFormattedAmount(sum.wins, sum.currency_code) }}
                                    </td>
                                    <td class="px-4 py-3 text-right font-bold">
                                        {{ showFormattedAmount(sum.hold, sum.currency_code) }}
                                    </td>
                                    <td class="px-4 py-3 text-right font-bold">
                                        {{ sum.stake_win_percentage }}
                                    </td>
                                    <td class="px-4 py-3 text-right font-bold">
                                        {{ showFormattedAmount(sum.avg_stake_per_spin, sum.currency_code) }}
                                    </td>
                                </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </template>
                <div v-else class="flex flex-wrap justify-center text-2xl text-white">
                    {{ $t('No Entries') }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Card from '../../Components/Card';
import DatePicker from '../../Components/DatePicker';
import SortIcons from '../../Components/SortIcons';

export default {
    name: "PerGame",
    components: {DatePicker, Card, SortIcons },
    props: {
        stats: Array|Object,
        sums: Array|Object
    },
    metaInfo() {
        return {
            title: this.title
        }
    },
    data: () => ({
        value: null,
        order: 'game_name',
        direction: 'asc',
    }),
    computed: {
        title() {
            return this.$t('Stats');
        },
        from() {
            if (this.value) {
                const split = this.value.split(' - ');
                return split[0];
            }
            return '';
        },
        to() {
            if (this.value) {
                const split = this.value.split(' - ');
                return split.length > 1 ? split[1] : split[0];
            }
            return '';
        }
    },
    created() {
        const urlParams = new URLSearchParams(window.location.search);
        let value = '';

        if (urlParams.has('from')) {
            value = urlParams.get('from');
        }

        if (urlParams.has('to')) {
            value = value + ' - ' + urlParams.get('to');
        }

        if (urlParams.has('order')) {
            this.order = urlParams.get('order');
        }

        if (urlParams.has('direction')) {
            this.direction = urlParams.get('direction');
        }

        this.value = value ? value : moment().format('DD.MM.YYYY') + ' - ' + moment().format('DD.MM.YYYY');
    },
    methods: {
        setOrder(column) {
            if (this.order === column) {
                if (this.direction === 'asc') {
                    this.direction = 'desc';
                } else {
                    this.direction = 'asc';
                }
            }

            this.order = column;
            this.$inertia.get(this.route('stats.perGame', {
                from: this.from,
                to: this.to,
                order: this.order,
                direction: this.direction,
            }));
        },
        handleChange(value) {
            const split = value.split(' - ');
            const from = split[0];
            const to = split.length > 1 ? split[1] : split[0];
            this.$inertia.get(this.route('stats.perGame', {
                from: from,
                to: to,
                order: this.order,
                direction: this.direction,
            }));
        },
        showFormattedAmount(amount, currencyCode) {
            return this.$formatCurrency(amount, currencyCode);
        },
    },
}
</script>

<style lang="scss" scoped>
tr:nth-child(even) {
    background-color: #f4f5f7;
}
</style>
